<template>
  <div class="min-h-screen bg-gray-50">
    <div
      class="max-w-none xl:max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-6 lg:py-8"
    >
      <Banner @goClicked="handleGoClicked" />

      <!-- Loading Overlay -->
      <LoadingOverlay
        :isVisible="isInitialLoading"
        :message="loadingMessage"
      />
      <!-- Global Toast -->
      <GlobalToast
        :message="globalMessage"
        @hide="hideGlobalMessage"
      />

      <!-- Controls -->
      <HeaderStudentModel
        :bannerGoClicked="bannerClicked"
        @search-text="handleSearch"
        @selected-class="handleClassSelect"
        @selected-year="handleYearSelect"
        :disabled="isLoading"
      />
      <!-- Stats Cards -->
      <StatsCards
        :students="students"
        :isLoading="isStatsLoading"
        :activeStudents="activeStudents"
        :graduatedStudents="graduatedStudents"
        :inactiveStudents="inactiveStudents"
      />
      <!-- Bulk Actions Bar -->
      <BulkActionsBar
        :selectedStudents="selectedStudents"
        :isLoading="isLoading"
        :isDeleting="isDeleting"
        :isBookingSmartCard="isBookingSmartCard"
        @clear-selection="clearSelection"
        @promote-selected-with-class="promoteSelectedStudents"
        @delete-selected="deleteSelectedStudents"
        @book-smart-card="bookSmartCardForSelected"
      />
      <!-- Student Table -->
      <StudentTable
        :students="students"
        :isLoading="isTableLoading"
        :isDeleting="isDeleting"
        :deletingStudentId="deletingStudentId"
        :selectedStudents="selectedStudents"
        :isAllSelected="isAllSelected"
        :tableLoadingMessage="tableLoadingMessage"
        @toggle-select-all="toggleSelectAll"
        @toggle-student-selection="toggleStudentSelection"
        @view-student="viewStudentDetails"
        @edit-student="editStudent"
        @delete-student="deleteStudent"
      />
      <!-- Edit Student Modal -->
      <EditStudentModal
        :show="showEditModal"
        :student="editingStudent"
        :isSaving="isSaving"
        :saveError="saveError"
        :saveSuccess="saveSuccess"
        @close="closeModal"
        @save="saveStudent"
        @photo-upload="handlePhotoUpload"
        @document-upload="handleDocumentUpload"
      />
      <!-- Delete and smartcard Confirmation Modal -->
      <ConfirmationPopupModal
        :show="showDeleteModal"
        title="Delete Confirmation"
        :description="
          deleteTarget.multiple
            ? `Are you sure you want to delete ${
                deleteTarget.count
              } selected student${
                deleteTarget.count > 1 ? 's' : ''
              }? This action cannot be undone.`
            : `Are you sure you want to delete ${deleteTarget.name}? This action cannot be undone.`
        "
        :names="
          deleteTarget.multiple ? deleteTarget.names : [deleteTarget.name]
        "
        itemLabel="Student"
        buttonText="Delete"
        :isDestructive="true"
        :isLoading="isDeleting"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />

      <ConfirmationPopupModal
        :show="showBookingModal"
        title="Book Smart Card"
        :description="`Book smart card for ${bookingTarget.count} student${
          bookingTarget.count > 1 ? 's' : ''
        }?`"
        :names="bookingTarget.names"
        itemLabel="Students"
        buttonText="Confirm"
        :isDestructive="false"
        :isLoading="isBookingSmartCard"
        @confirm="confirmBooking"
        @cancel="cancelBooking"
      >
        <!-- 💬 Slot content only for booking modal -->
        <template #body>
          <div class="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <p class="text-sm text-blue-900">
              <span class="font-semibold">⚠️ Important:</span>
              Only students with status
              <span class="font-bold text-blue-700">ACTIVE</span>
              cards will be booked.
            </p>
          </div>
        </template>
      </ConfirmationPopupModal>

      <!-- Pagination -->
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalResults="totalResults"
        :limit="limit"
        :isLoading="isTableLoading"
        @go-to-page="goToPage"
      />
      <!-- Student Details Modal -->
      <StudentDetailsModal
        :show="showDetailsModal"
        :student="viewingStudent"
        :isLoading="isDetailsLoading"
        @close="closeDetailsModal"
        @edit="editStudent"
        @view-document="viewDocument"
        @download-document="downloadDocument"
      />
      <!-- Document Viewer -->
      <DocumentViewer
        :show="showDocumentViewer"
        :documentUrl="currentDocumentUrl"
        :documentTitle="currentDocumentTitle"
        @close="closeDocumentViewer"
        @download="downloadDocument"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from "vue";
  import { useStore } from "vuex";
  import { useRoute, useRouter } from "vue-router";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import HeaderStudentModel from "@/components/Students/HeaderStudentModel.vue";
  import LoadingOverlay from "@/components/Students/LoadingOverlay.vue";
  import GlobalToast from "@/utils/GlobalToast.vue";
  import StatsCards from "@/components/Students/StatsCards.vue";
  import BulkActionsBar from "@/components/Students/BulkActions.vue";
  import StudentTable from "@/components/Students/StudentTable.vue";
  import EditStudentModal from "@/components/Students/EditStudentModal.vue";
  import ConfirmationPopupModal from "@/components/Students/ConfirmationPopupModal.vue";
  import Pagination from "@/components/Students/Pagination.vue";
  import StudentDetailsModal from "@/components/Students/StudentDetailsModal.vue";
  import DocumentViewer from "@/components/Students/DocumentViewerModal.vue";
  import Banner from "@/components/Banner/StudentBanner.vue";

  const store = useStore();
  const route = useRoute();
  const router = useRouter();

  const { showToast: showGlobalToast } = useGlobalToast();

  const students = computed(() => {
    return [...store.getters.allStudents]
      .filter((s) => s.rollNumber !== undefined && s.rollNumber !== null)
      .sort((a, b) => {
        const numA = parseInt(a.rollNumber);
        const numB = parseInt(b.rollNumber);
        return numA - numB;
      });
  });
  // Loading states
  const isInitialLoading = ref(false);
  const isLoading = ref(false);
  const isStatsLoading = ref(false);
  const isTableLoading = ref(false);
  const isSaving = ref(false);
  const isDeleting = ref(false);
  const isBookingSmartCard = ref(false);
  const loadingMessage = ref("Loading student data...");
  const tableLoadingMessage = ref("Loading students...");
  // View details
  const showDetailsModal = ref(false);
  const viewingStudent = ref(null);
  const isDetailsLoading = ref(false);
  const showDocumentViewer = ref(false);
  const currentDocumentUrl = ref("");
  const currentDocumentTitle = ref("");
  // Pagination
  const currentPage = ref(1);
  const limit = ref(20);
  const totalResults = computed(() => store.getters.getStudentPagination.total);
  const totalPages = computed(() => store.getters.getStudentPagination.pages);
  // Local state
  const editingStudent = ref(null);
  const showEditModal = ref(false);
  const error = ref("");
  const saveError = ref("");
  const saveSuccess = ref("");
  const selectedClassObject = ref(null);
  // Add these new refs:
  const showBookingModal = ref(false);
  const bookingTarget = ref({ count: 0, names: [] });

  // Delete state
  const showDeleteModal = ref(false);
  const deleteTarget = ref({ id: null, name: "" });
  const deletingStudentId = ref(null);

  const bannerClicked = ref(false);
  function handleGoClicked(value) {
    bannerClicked.value = value;
  }

  // banner emit value
  // Global message state
  const globalMessage = ref({
    show: false,
    type: "success",
    message: "",
  });
  // Filters
  const searchTerm = ref("");
  const selectedClass = ref(null);
  // Selection state
  const selectedStudents = ref([]);
  // Stats
  const activeStudents = computed(
    () => students.value.filter((s) => s.status === "active").length,
  );
  const graduatedStudents = computed(
    () => students.value.filter((s) => s.status === "passout").length,
  );
  const inactiveStudents = computed(
    () => students.value.filter((s) => s.status === "leaved").length,
  );
  const isAllSelected = computed(() => {
    return (
      students.value.length > 0 &&
      selectedStudents.value.length === students.value.length
    );
  });
  // File upload handlers
  // File upload handlers
  const handlePhotoUpload = (eventOrNull) => {
    if (eventOrNull === null) {
      // Photo removed
      if (editingStudent.value) editingStudent.value.photo = "";
      return;
    }

    const file = eventOrNull?.target?.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      saveError.value = "Photo file size must be less than 5MB";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (editingStudent.value) editingStudent.value.photo = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentUpload = (eventOrNull, documentType) => {
    if (eventOrNull === null) {
      // Document removed
      if (editingStudent.value && documentType)
        editingStudent.value[documentType] = "";
      return;
    }

    const file = eventOrNull?.target?.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      saveError.value = "Document file size must be less than 10MB";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (editingStudent.value && documentType)
        editingStudent.value[documentType] = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Global message functions
  const showGlobalMessage = (type, message) => {
    globalMessage.value = { show: true, type, message };
    setTimeout(() => {
      globalMessage.value.show = false;
    }, 5000);
  };
  const hideGlobalMessage = () => {
    globalMessage.value.show = false;
  };
  // Filter events
  const handleSearch = (value) => {
    searchTerm.value = value;
  };
  const handleClassSelect = (value) => {
    if (value && value._id) {
      selectedClass.value = value._id;
      selectedClassObject.value = value; // Keep the whole object
      console.log("Selected class ID:", value._id, "Name:", value.name);
    } else {
      console.warn("Invalid class selected:", value);
      selectedClass.value = null;
      selectedClassObject.value = null;
    }
  };

  // Bulk selection
  const toggleStudentSelection = (studentId) => {
    const index = selectedStudents.value.indexOf(studentId);
    if (index > -1) selectedStudents.value.splice(index, 1);
    else selectedStudents.value.push(studentId);
  };
  const toggleSelectAll = () => {
    selectedStudents.value = isAllSelected.value
      ? []
      : students.value.map((s) => s._id);
  };
  const clearSelection = () => (selectedStudents.value = []);
  // Promote

  const promoteSelectedStudents = async ({
    studentIds,
    classId,
    session,
    year,
  }) => {
    if (!studentIds || studentIds.length === 0) {
      showGlobalMessage(
        "error",
        "Please select at least one student to promote.",
      );
      return;
    }

    if (!classId) {
      showGlobalMessage(
        "error",
        "Please select a class to promote students to.",
      );
      return;
    }

    isLoading.value = true;
    try {
      const response = await store.dispatch("promoteStudentsByIds", {
        studentIds,
        classId,
        session: session || null,
        year: year ? Number(year) : null,
      });

      showGlobalMessage(
        "success",
        `Successfully promoted ${
          response.promotedCount || studentIds.length
        } student${
          (response.promotedCount || studentIds.length) > 1 ? "s" : ""
        }`,
      );

      await fetchStudents();
      clearSelection();
    } catch (err) {
      console.error("Promotion failed:", err);
      let errorMessage = "Failed to promote students";
      if (err.response) {
        const status = err.response.status;
        const message = err.response.data?.message || err.response.data?.error;
        switch (status) {
          case 400:
            errorMessage =
              message ||
              "Invalid promotion request. Please check student data.";
            break;
          case 403:
            errorMessage = "You don't have permission to promote students.";
            break;
          case 404:
            errorMessage = "Some selected students were not found.";
            break;
          case 409:
            errorMessage =
              message || "Some students cannot be promoted due to conflicts.";
            break;
          case 422:
            errorMessage =
              message ||
              "Promotion validation failed. Check student eligibility.";
            break;
          default:
            errorMessage =
              message || `Error ${status}: Failed to promote students`;
        }
      } else if (err.request) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else {
        errorMessage =
          err.message || "An unexpected error occurred during promotion.";
      }
      showGlobalMessage("error", errorMessage);
    } finally {
      isLoading.value = false;
    }
  };

  // Delete one student
  const deleteStudent = (studentId, studentName) => {
    deleteTarget.value = {
      id: studentId,
      name: studentName,
      multiple: false,
      count: 1,
      names: [studentName],
    };
    showDeleteModal.value = true;
  };

  const confirmDelete = async () => {
    isDeleting.value = true;
    try {
      if (deleteTarget.value.multiple) {
        // ===== BULK DELETE =====
        const idsToDelete = deleteTarget.value.ids || [];
        let successCount = 0;
        let failedCount = 0;

        for (const id of idsToDelete) {
          try {
            await store.dispatch("deleteUserById", id);
            successCount++;
          } catch (err) {
            failedCount++;
            console.error(`Failed to delete student ${id}:`, err);
          }
        }

        await fetchStudents();
        clearSelection();

        if (failedCount === 0) {
          showGlobalMessage(
            "success",
            `Successfully deleted ${successCount} student${
              successCount > 1 ? "s" : ""
            }`,
          );
        } else if (successCount === 0) {
          showGlobalMessage(
            "error",
            `Failed to delete all ${failedCount} student${
              failedCount > 1 ? "s" : ""
            }`,
          );
        } else {
          showGlobalMessage(
            "warning",
            `Deleted ${successCount} student${
              successCount > 1 ? "s" : ""
            }, but failed to delete ${failedCount} student${
              failedCount > 1 ? "s" : ""
            }`,
          );
        }
      } else {
        // ===== SINGLE DELETE =====
        deletingStudentId.value = deleteTarget.value.id;
        await store.dispatch("deleteUserById", deleteTarget.value.id);
        await fetchStudents();
        showGlobalMessage(
          "success",
          `${deleteTarget.value.name} has been deleted successfully`,
        );
      }

      showDeleteModal.value = false;
    } catch (err) {
      console.error("Delete failed:", err);
      let errorMessage = "Failed to delete student";
      if (err.response) {
        const status = err.response.status;
        const message = err.response.data?.message || err.response.data?.error;
        switch (status) {
          case 404:
            errorMessage =
              "Student not found. They may have already been deleted.";
            break;
          case 403:
            errorMessage = "You don't have permission to delete this student.";
            break;
          case 409:
            errorMessage =
              "Cannot delete student due to existing dependencies.";
            break;
          default:
            errorMessage =
              message || `Error ${status}: Failed to delete student`;
        }
      } else if (err.request) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }
      showGlobalMessage("error", errorMessage);
    } finally {
      isDeleting.value = false;
      deletingStudentId.value = null;
    }
  };

  const cancelDelete = () => {
    showDeleteModal.value = false;
    deleteTarget.value = {
      id: null,
      name: "",
      multiple: false,
      count: 0,
      names: [],
    };
  };

  // Bulk delete (uses popup modal)
  const deleteSelectedStudents = async () => {
    if (!selectedStudents.value.length) return;

    const selectedCount = selectedStudents.value.length;
    const selectedNames = students.value
      .filter((s) => selectedStudents.value.includes(s._id))
      .map((s) => s.name);

    deleteTarget.value = {
      multiple: true,
      count: selectedCount,
      names: selectedNames,
      ids: [...selectedStudents.value],
    };

    showDeleteModal.value = true;
  };

  // Fetch students
  const fetchStudents = async () => {
    const isInitialLoad =
      currentPage.value === 1 && students.value.length === 0;
    isInitialLoading.value = isInitialLoad;
    isTableLoading.value = !isInitialLoad;
    tableLoadingMessage.value = isInitialLoad
      ? "Loading student directory..."
      : `Loading page ${currentPage.value}...`;
    try {
      isLoading.value = true;
      await store.dispatch("fetchStudents", {
        search: searchTerm.value,
        class: selectedClass.value,
        year:
          selectedClassObject.value?.name?.toLowerCase() === "passout"
            ? selectedYear.value || ""
            : "", // Only send year when class name is 'passout'
        page: currentPage.value,
        limit: limit.value,
      });
      clearSelection();
    } catch (err) {
      error.value = "Failed to fetch students.";
      console.error(err);
      showGlobalMessage(
        "error",
        "Failed to load students. Please refresh the page.",
      );
    } finally {
      isLoading.value = false;
      isTableLoading.value = false;
      isInitialLoading.value = false;
    }
  };

  // Watch filters
  let searchTimeout;
  const selectedYear = ref(null);
  function handleYearSelect(year) {
    selectedYear.value = year;
  }
  watch(selectedYear, () => {
    currentPage.value = 1;
    fetchStudents();
  });
  watch(searchTerm, () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      currentPage.value = 1;
      fetchStudents();
    }, 300);
  });
  watch(selectedClass, () => {
    currentPage.value = 1;
    fetchStudents();
  });
  // Edit
  const editStudent = (student) => {
    if (!student) {
      console.warn("Invalid student object passed to editStudent");
      return;
    }
    editingStudent.value = {
      ...editingStudent.value,
      ...student,
      email: student.email || null,
      enrollmentDate: student.enrollmentDate
        ? new Date(student.enrollmentDate).toISOString().split("T")[0]
        : editingStudent.value?.enrollmentDate || "",
      dob: student.dob
        ? new Date(student.dob).toISOString().split("T")[0]
        : editingStudent.value?.dob || "",

      // Ensure 'class' is always an object with 'name' and '_id' properties
      class:
        student.class && typeof student.class === "object"
          ? { ...student.class } // Deep copy to avoid direct mutation of prop
          : { name: student.class || "", _id: null }, // Default to object with name (if string) or empty
      photo: (student.photo ?? editingStudent.value?.photo) || "",
      aadhaarCard:
        (student.aadhaarCard ?? editingStudent.value?.aadhaarCard) || "",
      birthCertificate:
        (student.birthCertificate ?? editingStudent.value?.birthCertificate) ||
        "",
      transferCertificate:
        (student.transferCertificate ??
          editingStudent.value?.transferCertificate) ||
        "",
      marksheet: (student.marksheet ?? editingStudent.value?.marksheet) || "",
      // ✅ Include passout fields
      passoutYear: student.passoutYear || "",
      session: student.session || "",
    };
    showEditModal.value = true;
  };

  const saveStudent = async () => {
    saveError.value = "";
    saveSuccess.value = "";
    if (!editingStudent.value.name?.trim()) {
      saveError.value = "Student name is required";
      showGlobalToast("error", "Student name is required");
      return;
    }
    // if (!editingStudent.value.dob) {
    //   saveError.value = "DOB is required";
    //   showGlobalToast("error", "DOB is required");
    //   return;
    // }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      editingStudent.value.email &&
      !emailRegex.test(editingStudent.value.email.trim())
    ) {
      saveError.value = "Invalid email address";
      showGlobalToast("error", "Invalid email address");
      return;
    }
    isSaving.value = true;
    try {
      const updates = {
        name: editingStudent.value.name.trim(),
        email: editingStudent.value.email
          ? editingStudent.value.email.trim()
          : null,
        phone: editingStudent.value.phone?.trim() || "",
        address: editingStudent.value.address?.trim() || "",
        rollNumber: editingStudent.value.rollNumber?.trim() || "",
        status: editingStudent.value.status,
        enrollmentDate: editingStudent.value.enrollmentDate || null,
        dob: editingStudent.value.dob || null,
        subDistrict: editingStudent.value.subDistrict?.trim() || null,
        city: editingStudent.value.city?.trim() || null,
        state: editingStudent.value.state?.trim() || null,
        pincode: editingStudent.value.pincode?.trim() || null,
      };

      // Handle class properly - send the _id if it exists, otherwise send the name
      if (
        typeof editingStudent.value.class === "object" &&
        editingStudent.value.class
      ) {
        if (editingStudent.value.class._id) {
          // If we have an _id, send it to the backend
          updates.class = editingStudent.value.class._id;
        } else {
          // If no _id, send the name (for new class creation)
          updates.class = editingStudent.value.class.name || "";
        }
      } else {
        // If it's just a string, send it as is
        updates.class = editingStudent.value.class || "";
      }

      // 🔥 Only add passoutYear/session if status is passout
      if (editingStudent.value.status?.toLowerCase?.() === "passout") {
        if (
          editingStudent.value.passoutYear &&
          !isNaN(editingStudent.value.passoutYear)
        ) {
          updates.passoutYear = Number(editingStudent.value.passoutYear);
        }
        if (editingStudent.value.session) {
          updates.session = editingStudent.value.session;
        }
      }

      const documentFields = [
        "photo",
        "aadhaarCard",
        "birthCertificate",
        "transferCertificate",
        "marksheet",
      ];
      for (const field of documentFields) {
        // Send null if empty string
        updates[field] =
          editingStudent.value[field] &&
          editingStudent.value[field].trim() !== ""
            ? editingStudent.value[field]
            : null;
      }

      await store.dispatch("editUserById", {
        userId: editingStudent.value._id,
        updates,
      });
      await fetchStudents();
      saveSuccess.value = "Student updated successfully!";
      showEditModal.value = false;
      showDetailsModal.value = false;
      showGlobalMessage(
        "success",
        `${editingStudent.value.name} has been updated successfully`,
      );
      saveSuccess.value = "";
    } catch (err) {
      console.error("Save error:", err);

      let errorMessage = "An unexpected error occurred. Please try again.";

      if (err.response) {
        const status = err.response.status;
        const message = err.response.data?.message || err.response.data?.error;

        switch (status) {
          case 400:
            errorMessage =
              message || "Invalid student data. Please check all fields.";
            break;
          case 409:
            errorMessage =
              message ||
              "A student with this email or roll number already exists.";
            break;
          case 422:
            errorMessage =
              message || "Please check your input data for errors.";
            break;
          default:
            errorMessage =
              message || `Error ${status}: Failed to save student information.`;
        }
      } else if (err.request) {
        errorMessage =
          "Network error. Please check your internet connection and try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      saveError.value = errorMessage;
      showGlobalToast("error", errorMessage);
    } finally {
      isSaving.value = false;
    }
  };

  const closeModal = () => {
    if (!isSaving.value) {
      showEditModal.value = false;
      saveError.value = "";
      saveSuccess.value = "";
    }
  };
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value && !isTableLoading.value) {
      currentPage.value = page;
      tableLoadingMessage.value = `Loading page ${page}...`;
      fetchStudents();
    }
  };
  // View Student Details
  const viewStudentDetails = async (student) => {
    isDetailsLoading.value = true;
    showDetailsModal.value = true;
    try {
      const detailedStudent = await store.dispatch(
        "fetchUserById",
        student._id,
      );
      viewingStudent.value = {
        ...detailedStudent,
        aadhaarCard: detailedStudent.aadhaarCard || "",
        birthCertificate: detailedStudent.birthCertificate || "",
        transferCertificate: detailedStudent.transferCertificate || "",
        marksheet: detailedStudent.marksheet || "",
      };
    } catch (err) {
      console.error("Failed to load student details:", err);
      showGlobalMessage("error", "Failed to load student details");
    } finally {
      isDetailsLoading.value = false;
    }
  };
  const closeDetailsModal = () => {
    showDetailsModal.value = false;
    viewingStudent.value = null;
  };
  const viewDocument = (documentUrl, title) => {
    const fullUrl = documentUrl;
    currentDocumentUrl.value = fullUrl;
    currentDocumentTitle.value = title;
    showDocumentViewer.value = true;
  };
  const downloadDocument = async (documentUrl, filename) => {
    try {
      showGlobalMessage("info", "Preparing document for download...");
      const response = await fetch(documentUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the download attribute
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url); // Clean up the object URL
      showGlobalMessage("success", "Document download started");
    } catch (err) {
      console.error("Download failed:", err);
      showGlobalMessage(
        "error",
        "Failed to download document. Please try again.",
      );
    }
  };
  // Book Smart Card Handler
  const bookSmartCardForSelected = async () => {
    if (!selectedStudents.value.length) {
      showGlobalMessage(
        "error",
        "Please select at least one student to book smart card.",
      );
      return;
    }

    // Show confirmation modal with student names
    const selectedCount = selectedStudents.value.length;
    const selectedNames = students.value
      .filter((s) => selectedStudents.value.includes(s._id))
      .map((s) => s.name);

    bookingTarget.value = {
      count: selectedCount,
      names: selectedNames,
    };
    showBookingModal.value = true;
  };
  const confirmBooking = async () => {
    isBookingSmartCard.value = true;
    try {
      await store.dispatch("bookSmartCard", {
        userId: selectedStudents.value,
      });

      showGlobalMessage(
        "success",
        `Smart card booking initiated for ${
          selectedStudents.value.length
        } student${selectedStudents.value.length > 1 ? "s" : ""}`,
      );

      await fetchStudents();
      clearSelection();
    } catch (err) {
      console.error("Book smart card failed:", err);
      let errorMessage = "Failed to book smart card";
      if (err.response) {
        const status = err.response.status;
        const message = err.response.data?.message || err.response.data?.error;
        switch (status) {
          case 400:
            errorMessage =
              message || "Invalid request. Please check selected students.";
            break;
          case 403:
            errorMessage = "You don't have permission to book smart cards.";
            break;
          case 404:
            errorMessage = "Some selected students were not found.";
            break;
          default:
            errorMessage =
              message || `Error ${status}: Failed to book smart card`;
        }
      } else if (err.request) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }
      showGlobalMessage("error", errorMessage);
    } finally {
      isBookingSmartCard.value = false;
      showBookingModal.value = false;
    }
  };

  const cancelBooking = () => {
    showBookingModal.value = false;
    bookingTarget.value = { count: 0, names: [] };
  };
  const closeDocumentViewer = () => {
    showDocumentViewer.value = false;
    currentDocumentUrl.value = "";
    currentDocumentTitle.value = "";
  };
  onMounted(async () => {
    if (route.query.classId) {
      selectedClass.value = route.query.classId;
    }

    isInitialLoading.value = true;

    // Load classes first
    await store.dispatch("fetchAllClasses");

    // 🚀 FAST PATH — open modal immediately if userId exists
    if (route.query.userId) {
      const userId = route.query.userId;

      // Open modal instantly
      showDetailsModal.value = true;

      // Inject cached student if available
      const cached = store.getters.allStudents?.find((s) => s._id === userId);
      if (cached) viewingStudent.value = cached;

      // Background fetch (non-blocking)
      queueMicrotask(async () => {
        try {
          isDetailsLoading.value = true;
          const detailed = await store.dispatch("fetchUserById", userId);
          viewingStudent.value = detailed;
        } finally {
          isDetailsLoading.value = false;
          router.replace({ query: {} });
        }
      });
    }

    // Finally load students table
    await fetchStudents();
  });
</script>
